import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Megaphone, Music2, Clapperboard, Code2, Building2, Landmark, Hammer, PenTool, BookOpen, MapPin, Users, Search } from "lucide-react";
import { DISCIPLINES, LGAS } from "@/lib/data";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const ICONS = { Megaphone, Music2, Clapperboard, Code2, Building2, Landmark, Hammer, PenTool, BookOpen };
const HERO = "https://images.unsplash.com/photo-1571990455341-c06400a41071?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwY3JlYXRpdmUlMjBhcnRzJTIwY3VsdHVyZSUyMGZlc3RpdmFsJTIwYXJ0JTIwZ2FsbGVyeSUyMGFydGlzdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4ODYzMTMwMXww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [stats, setStats] = useState({ members: 0 });
  useEffect(() => {
    api.get("/members").then((r) => setStats({ members: r.data.length })).catch(() => {});
  }, []);

  return (
    <main className="pt-16">
      {/* HERO */}
      <section className="relative grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C0E]/70 via-[#0B0C0E]/85 to-[#0B0C0E]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <div className="overline-label mb-5">13 Local Government Areas · One Creative State</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Where Nasarawa's <span className="brand-text-gradient">creatives</span> get discovered.
            </h1>
            <p className="mt-6 text-lg text-zinc-300 max-w-xl leading-relaxed">
              A statewide movement for artists, designers, performers, filmmakers and innovators.
              Register, receive your digital membership card, and become searchable to clients looking to hire.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-register-button">
                <Button size="lg" className="bg-[#E6197E] hover:bg-[#C4136B] text-white rounded-full px-8 h-12 text-base font-semibold">
                  Become a Member <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/directory">
                <Button size="lg" variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 rounded-full px-8 h-12 text-base">
                  <Search className="w-4 h-4 mr-2" /> Browse Directory
                </Button>
              </Link>
            </div>
            <div className="mt-12 flex gap-10">
              <div><div className="font-heading text-3xl font-extrabold text-white">{stats.members}+</div><div className="text-sm text-zinc-400 mt-1">Registered creatives</div></div>
              <div><div className="font-heading text-3xl font-extrabold text-white">13</div><div className="text-sm text-zinc-400 mt-1">LGAs covered</div></div>
              <div><div className="font-heading text-3xl font-extrabold text-white">15+</div><div className="text-sm text-zinc-400 mt-1">Creative fields</div></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DISCIPLINES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="overline-label mb-3">Creative Disciplines</div>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">Nine pillars of the Nasarawa creative economy</h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {DISCIPLINES.map((d, i) => {
            const Icon = ICONS[d.icon];
            return (
              <motion.div key={d.category} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className={`relative rounded-2xl border border-white/10 bg-gradient-to-br ${d.accent} p-6 overflow-hidden hover:border-[#E6197E]/40 transition-colors group`}>
                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  {Icon && <Icon className="w-6 h-6 text-[#E6197E]" />}
                </div>
                <h3 className="font-heading text-lg font-semibold leading-tight">{d.category}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{d.desc}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {d.subcategories.map((s) => (
                    <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-zinc-300 border border-white/10">{s}</span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* LGA STRIP */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        <div className="rounded-3xl border border-white/10 bg-[#131519] p-8 sm:p-12 relative grain overflow-hidden">
          <div className="flex items-center gap-2 mb-6"><MapPin className="w-5 h-5 text-[#E6197E]" /><div className="overline-label">Across the State</div></div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8">Find creatives in your Local Government Area</h2>
          <div className="flex flex-wrap gap-2.5">
            {LGAS.map((l) => (
              <Link key={l} to={`/directory?lga=${encodeURIComponent(l)}`}
                className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-300 hover:bg-[#E6197E] hover:text-white hover:border-[#E6197E] transition-colors">
                {l}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-8">
        <div className="rounded-3xl bg-[#E6197E] p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <Users className="w-10 h-10 mx-auto mb-5 text-white" />
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-white max-w-2xl mx-auto">Your talent deserves to be seen.</h2>
          <p className="mt-4 text-white/90 max-w-lg mx-auto">Join the movement, get your digital membership card, and be part of Nasarawa's creative directory.</p>
          <Link to="/register" className="inline-block mt-8">
            <Button size="lg" className="bg-[#0B0C0E] hover:bg-black text-white rounded-full px-8 h-12 text-base font-semibold">Register Now <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
